import React, { useEffect, useState } from 'react';
import { getAllStackEntries } from '../services/api';
import { PageHeading, Section, Heading, Text, PlainTable, PopupWindow } from '../components/ui';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { useMenuScheduling } from '../context/menuSchedulingContext';
import { useCompare } from '../context/CompareContext';
import SignedImage from '../components/SignedImage';
import PixelArtLoader from '../components/PixelArtLoader';
import FloatingCreateButton from '../components/FloatingCreateButton';

const Explore = () => {
    const { user, userProfile } = useAuth();
    const { isSchedulingMode, toggleRecipeSelection, selectedRecipeIds } = useMenuScheduling();
    const { showCompareSelectionModal, toggleRecipeSelection: toggleCompareRecipe, selectedRecipes: compareSelectedRecipes } = useCompare();
    const [recipes, setRecipes] = useState([]);
    const [filteredRecipes, setFilteredRecipes] = useState([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [searchTerm, setSearchTerm] = useState('');
    const [controlsExpanded, setControlsExpanded] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    
    // Filter state
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [cookingTimeFilter, setCookingTimeFilter] = useState('all');
    
    // Animation state
    const [animationPhase, setAnimationPhase] = useState(0);
    const [showContent, setShowContent] = useState(false);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [pageLoaded, setPageLoaded] = useState(false);
    
    // Popup state
    const [openPopups, setOpenPopups] = useState([]);
    const navigate = useNavigate();

    const controlsPanelId = 'explore-controls-panel';

    // Cooking tips array
    const cookingTips = [
        "Peel ginger with a spoon - it follows every contour and minimizes waste",
        "Use an immersion blender for soups, sauces, and mayonnaise - much easier to clean",
        "Keep a small strainer by the stove for quick citrus squeezing without seeds",
        "Use that same strainer to make perfectly shaped poached eggs",
        "Work like a factory line - do all similar tasks together for efficiency",
        "Keep a garbage bowl and bench scraper near your workstation",
        "Freeze wine in ice cube trays for easy pan sauce additions",
        "Freeze liquids flat for faster freezing and defrosting",
        "Defrost meat on aluminum trays - cuts time by 30%",
        "Slice avocados in their skins, then scoop out with a spoon",
        "Buy pre-peeled garlic - it tastes just fine and saves time",
        "Read the entire recipe first before starting to cook",
        "Use a scale for baking - it's more accurate than cups",
        "The microplane is your friend for zest, ginger, garlic, and cheese",
        "Make vinaigrettes in squeeze bottles with recipe lines drawn on",
        "Buy deli containers with matching lids for organized storage",
        "Taste meatloaf mixtures before shaping - fry a small piece first",
        "Partially freeze meat before cutting for cleaner slices",
        "Keep your knives sharp - it's safer and more pleasurable",
        "Save Parmesan rinds in the freezer for flavoring soups and stews",
        "Use mozzarella liquid as pasta sauce base",
        "Store greens with damp paper towels to extend freshness",
        "Don't be afraid of salt, but don't forget the acid either",
        "Use egg shells to remove egg shell fragments"
    ];

    // Animation control
    const [shouldShowAnimation, setShouldShowAnimation] = useState(true);

    // Check if animation has been shown this session
    useEffect(() => {
        // Prevent multiple executions
        if (animationPhase > 0 || showContent) {
            return;
        }

        const animationData = sessionStorage.getItem('exploreAnimationShown');
        const currentTime = Date.now();
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        
        let hasSeenAnimation = false;
        let shouldSkip = false;
        
        if (animationData) {
            try {
                const data = JSON.parse(animationData);
                const timeSinceLastSeen = currentTime - data.timestamp;
                
                // Only skip if animation was seen within the last hour
                if (timeSinceLastSeen < oneHour) {
                    hasSeenAnimation = true;
                    shouldSkip = true;
                }
            } catch (e) {
                // If parsing fails, treat as not seen
                console.log('Failed to parse animation data, showing animation');
            }
        }
        
        
        if (shouldSkip) {
            // Animation already shown recently, skip it but add smooth page load
            setShouldShowAnimation(false);
            setAnimationPhase(4);
            setAnimationComplete(true);
            
            // Dispatch event immediately since animation is skipped
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('exploreAnimationComplete'));
            }
            
            // Add smooth page load animation
            setTimeout(() => {
                setPageLoaded(true);
                setShowContent(true);
            }, 100);
        } else {
            // Show animation (first time or expired)
            
            // Pick a random tip for this session
            const randomTipIndex = Math.floor(Math.random() * cookingTips.length);
            setCurrentTipIndex(randomTipIndex);
            
            // Start showing dot pattern immediately
            setTimeout(() => {
                setAnimationPhase(1);
            }, 0);
            
            // Start showing tip after 0.5 seconds
            setTimeout(() => {
                setAnimationPhase(2);
            }, 500);
            
            // Start overlay fade-out after 4 seconds
            setTimeout(() => {
                setAnimationPhase(3);
            }, 4000);
            
            // Show content after 4.3 seconds (overlapping with fade)
            setTimeout(() => {
                setAnimationPhase(4);
                setShowContent(true);
                
                // Set storage only after animation starts
                sessionStorage.setItem('exploreAnimationShown', JSON.stringify({
                    timestamp: currentTime,
                    shown: true
                }));
            }, 4300);
            
            // Complete fade-out after transition
            setTimeout(() => {
                setAnimationComplete(true);
                // Dispatch event to notify header toggle that animation is complete
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('exploreAnimationComplete'));
                }
            }, 5300);
        }
    }, [animationPhase, showContent]);

    // Cleanup effect to clear intervals
    useEffect(() => {
        return () => {
            if (window.tipInterval) {
                clearInterval(window.tipInterval);
            }
        };
    }, []);

    // Function to clear session storage and restart animation
    const clearAnimationSession = () => {
        sessionStorage.removeItem('exploreAnimationShown');
        setShouldShowAnimation(true);
        setAnimationPhase(0);
        setShowContent(false);
        setAnimationComplete(false);
        
        // Pick a random tip for testing
        const randomTipIndex = Math.floor(Math.random() * cookingTips.length);
        setCurrentTipIndex(randomTipIndex);
        
        // Start animation sequence immediately
        setTimeout(() => {
            setAnimationPhase(1);
        }, 100);
        setTimeout(() => {
            setAnimationPhase(2);
            setShowContent(true);
        }, 2000);
        
        // Complete fade-out after transition
        setTimeout(() => {
            setAnimationComplete(true);
        }, 3000);
    };

    // Function to skip animation and go directly to content
    const skipAnimation = () => {
        // Clear tip cycling if running
        if (window.tipInterval) {
            clearInterval(window.tipInterval);
        }
        
        setAnimationPhase(4);
        setShowContent(true);
        setAnimationComplete(true);
        
        // Set session storage to prevent animation from showing again
        const currentTime = Date.now();
        sessionStorage.setItem('exploreAnimationShown', JSON.stringify({
            timestamp: currentTime,
            shown: true
        }));
    };

    // Function to format cooking time
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    useEffect(() => {
        const fetchRecipes = async () => {
            try {
                const allRecipes = await getAllStackEntries();
                setRecipes(allRecipes);
                setFilteredRecipes(allRecipes);
            } catch (err) {
                console.error('Error fetching recipes:', err);
                setError('Failed to load recipes');
            } finally {
                setLoading(false);
            }
        };

        fetchRecipes();
    }, []);

    // Filter recipes based on search term, category, and cooking time
    useEffect(() => {
        // Don't filter if we don't have recipes yet
        if (recipes.length === 0) {
            setFilteredRecipes([]);
            setIsFiltering(false);
            return;
        }

        // Set filtering state immediately to prevent flash
        setIsFiltering(true);

        // Process filtering immediately without timeout to prevent flash
        let filtered = recipes;

        // Live Sessions v2: Show Gold Standard recipes instead of newest forks
        // Group recipes by chain_root_id and prioritize Gold Standard (is_gold_standard = true)
        const chainGroups = new Map();
        filtered.forEach(recipe => {
            const chainId = recipe.chain_root_id;
            const existingRecipe = chainGroups.get(chainId);
            
            if (!existingRecipe) {
                chainGroups.set(chainId, recipe);
            } else {
                // Prioritize Gold Standard recipes
                const isCurrentGoldStandard = recipe.is_gold_standard === true;
                const isExistingGoldStandard = existingRecipe.is_gold_standard === true;
                
                if (isCurrentGoldStandard && !isExistingGoldStandard) {
                    // Current recipe is Gold Standard, existing is not - use current
                    chainGroups.set(chainId, recipe);
                } else if (!isCurrentGoldStandard && isExistingGoldStandard) {
                    // Existing recipe is Gold Standard, current is not - keep existing
                    // Do nothing, keep existing
                } else {
                    // Both or neither are Gold Standard - use most recent
                    if (new Date(recipe.created_at) > new Date(existingRecipe.created_at)) {
                        chainGroups.set(chainId, recipe);
                    }
                }
            }
        });
        
        // Convert back to array with Gold Standard recipes prioritized
        filtered = Array.from(chainGroups.values());

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(recipe =>
                (recipe.name || recipe.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (recipe.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (recipe.creator_name || '').toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(recipe => 
                recipe.category && recipe.category.toLowerCase() === categoryFilter.toLowerCase()
            );
        }

        // Cooking time filter
        if (cookingTimeFilter !== 'all') {
            filtered = filtered.filter(recipe => {
                const cookingTime = recipe.cooking_time || 0;
                switch (cookingTimeFilter) {
                    case 'quick':
                        return cookingTime <= 30;
                    case 'medium':
                        return cookingTime > 30 && cookingTime <= 60;
                    case 'long':
                        return cookingTime > 60;
                    default:
                        return true;
                }
            });
        }

        setFilteredRecipes(filtered);
        setIsFiltering(false);
    }, [recipes, searchTerm, categoryFilter, cookingTimeFilter]);

    // Close all popups when ESC is pressed
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && openPopups.length > 0) {
                // Close the most recent popup
                setOpenPopups(prev => prev.slice(0, -1));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [openPopups.length]);

    const clearFilters = () => {
        setSearchTerm('');
        setCategoryFilter('all');
        setCookingTimeFilter('all');
    };

    const hasActiveFilters = Boolean(
        searchTerm ||
        categoryFilter !== 'all' ||
        cookingTimeFilter !== 'all'
    );

    // Handle recipe row click to open popup (only in list mode)
    const handleRecipeClick = (recipe, e) => {
        // Don't open popup if clicking on action buttons
        if (e.target.closest('button') || e.target.closest('a')) {
            return;
        }
        
        // If in scheduling mode, toggle recipe selection instead of navigating
        if (isSchedulingMode) {
            e.preventDefault();
            e.stopPropagation();
            toggleRecipeSelection(recipe.id, recipe);
            return;
        }
        
        // If in compare selection mode, toggle recipe selection instead of navigating
        if (showCompareSelectionModal) {
            e.preventDefault();
            e.stopPropagation();
            toggleCompareRecipe(recipe);
            return;
        }
        
        // Don't open popups on small screens (mobile)
        if (window.innerWidth < 640) {
            // On small screens, navigate to recipe detail page
            navigate(`/cache/${recipe.id}`);
            return;
        }

        // Only open popups in list mode, not grid mode
        if (viewMode !== 'list') {
            // In grid mode, navigate to recipe detail page using React Router
            navigate(`/cache/${recipe.id}`);
            return;
        }

        // Only allow popups for recipes with images
        if (!recipe.images || recipe.images.length === 0) {
            // If no images, navigate to recipe detail page
            navigate(`/cache/${recipe.id}`);
            return;
        }
        
        // Check if popup is already open for this recipe
        if (openPopups.some(popup => popup.recipe.id === recipe.id)) {
            return;
        }
        
        // Calculate diagonal position starting from current scroll position
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        const offset = openPopups.length * 32; // Increased offset for diagonal spacing
        
        // Start diagonal pattern from current scroll position
        const baseX = scrollX + 96; // Start 96px from left edge
        const baseY = scrollY + 96; // Start 96px from top edge
        
        // Create diagonal pattern: each popup moves down and right
        const left = baseX + offset;
        const top = baseY + offset;
        
        // Ensure popup doesn't go off-screen
        const popupWidth = 384; // w-96 = 384px
        const popupHeight = 400; // approximate height
        
        // If popup would go off the right edge, reset to left side but continue diagonal
        let finalLeft = left;
        let finalTop = top;
        
        if (left + popupWidth > window.innerWidth + scrollX - 24) {
            // Reset to left side, but continue diagonal pattern
            finalLeft = scrollX + 96;
            finalTop = top + 32; // Continue diagonal downward
        }
        
        // If popup would go off the bottom edge, reset to top but continue diagonal
        if (top + popupHeight > window.innerHeight + scrollY - 24) {
            // Reset to top, but continue diagonal pattern
            finalTop = scrollY + 96;
            finalLeft = left + 32; // Continue diagonal rightward
        }
        
        const newPopup = {
            recipe,
            id: Date.now(),
            style: {
                top: `${finalTop}px`,
                left: `${finalLeft}px`,
            }
        };
        
        setOpenPopups(prev => [...prev, newPopup]);
    };

    // Close specific popup
    const closePopup = (popupId) => {
        setOpenPopups(prev => prev.filter(popup => popup.id !== popupId));
    };

    const RecipeGridCard = ({ recipe }) => {
        // Parse ingredients and instructions if they're JSON strings
        const parsedIngredients = typeof recipe.ingredients === 'string' ? 
            JSON.parse(recipe.ingredients || '[]') : (recipe.ingredients || []);
        
        // Determine display name and styling for creator/forker
        // Explore page: No stack effect for any recipes - show normal cards
        const isForked = false; // Always false on explore page to remove stack effect
        // For root recipes with forks, show original creator name, not forker name
        const isRootWithForks = recipe.is_original && recipe.forks_count > 0;
        const displayName = isRootWithForks ? (recipe.creator_name || 'Unknown') : 
                           (recipe.forker_name ? recipe.forker_name : (recipe.creator_name || 'Unknown'));
        const nameStyle = recipe.forker_name ? 'text-blue-600' : 'text-neutral-900 opacity-60';
        
        const isSelected = isSchedulingMode && selectedRecipeIds.has(recipe.id);
        const isCompareSelected = showCompareSelectionModal && compareSelectedRecipes.some(r => r.id === recipe.id);
        
        return (
            <div className="relative">
                {/* Main recipe card */}
                <div className={`aspect-square bg-neutral-0 hairline transition-all duration-200 cursor-pointer group flex flex-col relative hover:shadow-popup ${(isSelected || isCompareSelected) ? 'ring-2 ring-[#1b263b] bg-[#EEEEE7]' : ''}`}
                    onClick={(e) => handleRecipeClick(recipe, e)}
                >
                <div className="flex-1 overflow-hidden">
                    <SignedImage
                        src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                        alt={recipe.name || recipe.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        category={recipe.category}
                    />
                </div>
                
                <div className="p-3 flex-shrink-0">
                    <Text className="font-medium text-neutral-900 mb-1 group-hover:opacity-60 transition-opacity text-sm leading-tight">
                        {recipe.name || recipe.title}
                    </Text>
                    
                    {(recipe.description || recipe.category) && (
                        <Text className="text-xs text-neutral-900 opacity-60 mb-2 line-clamp-2 leading-tight">
                            {recipe.description || recipe.category}
                        </Text>
                    )}
                    
                    <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider mb-2">
                        <span className="text-neutral-900 opacity-60">{parsedIngredients.length ? `${parsedIngredients.length} ING` : '—'}</span>
                        <span className={`${nameStyle} font-medium`}>{displayName}</span>
                    </div>
                    
                    {recipe.category && (
                        <div className="flex gap-1 flex-wrap">
                            <span className="px-1 md:px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                {recipe.category}
                            </span>
                            {recipe.cooking_time && (
                                <span className="px-1 md:px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                    {formatCookingTime(recipe.cooking_time)}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                </div>
            </div>
        );
    };

    const RecipeListItem = ({ recipe }) => {
        // Parse ingredients if they're JSON strings
        const parsedIngredients = typeof recipe.ingredients === 'string' ? 
            JSON.parse(recipe.ingredients || '[]') : (recipe.ingredients || []);
        
        // Determine display name and styling for creator/forker/transfer
        const isForked = recipe.forker_name && !recipe.is_original && !recipe.is_transfer;
        const isTransferred = recipe.is_transfer;
        const displayName = isForked ? recipe.forker_name : (isTransferred ? recipe.creator_name : (recipe.author || recipe.creator_name || 'Unknown'));
        const nameStyle = isForked ? 'text-blue-600 font-medium' : (isTransferred ? 'text-blue-600 font-medium' : 'text-neutral-900 opacity-60');
            
        return (
            <div className={`grid grid-cols-4 gap-4 py-2 px-0 hover:bg-neutral-50 transition-colors duration-120 cursor-pointer sm:border-none border border-neutral-200 ${isTransferred ? 'relative' : ''}`}
                style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}
                onClick={(e) => handleRecipeClick(recipe, e)}
            >
                {/* Webpage browser bar for transferred recipes */}
                {isTransferred && (
                    <div className="absolute top-0 left-0 right-0 h-6 bg-neutral-200 border-b border-neutral-900 flex items-center px-2 z-10">
                        <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-red-400 rounded-full border border-neutral-900"></div>
                            <div className="w-2 h-2 bg-yellow-400 rounded-full border border-neutral-900"></div>
                            <div className="w-2 h-2 bg-green-400 rounded-full border border-neutral-900"></div>
                        </div>
                        <div className="flex-1 mx-3">
                            <div className="h-4 bg-white border border-neutral-300 rounded-sm flex items-center px-2">
                                <span className="text-xs text-neutral-600 truncate">{recipe.source_url ? new URL(recipe.source_url).hostname : 'recipe website'}</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`flex items-center min-w-0 pl-2 ${isTransferred ? 'pt-6' : ''}`}>
                    <Text className="font-medium text-neutral-900 truncate">
                        {recipe.name || recipe.title}
                    </Text>
                </div>
                
                <div className={`flex items-center font-mono text-sm text-neutral-900 min-w-0 pl-2 ${isTransferred ? 'pt-6' : ''}`}>
                    <div className="flex gap-1 min-w-0">
                        {recipe.category && (
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                {recipe.category}
                            </span>
                        )}
                        {recipe.cooking_time && (
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                {formatCookingTime(recipe.cooking_time)}
                            </span>
                        )}
                        {!recipe.category && !recipe.cooking_time && (
                            <span className="text-neutral-900 opacity-40 text-xs">—</span>
                        )}
                    </div>
                </div>
                
                <div className={`flex items-center pl-2 ${isTransferred ? 'pt-6' : ''}`}>
                    <span className={`font-mono text-sm ${nameStyle}`}>
                        {displayName}
                    </span>
                </div>
                
                <div className={`flex items-center pl-2 ${isTransferred ? 'pt-6' : ''}`}>
                    <div className="flex space-x-1">
                        <Link 
                            to={`/recipes/${recipe.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-mono uppercase tracking-wider bg-neutral-900 text-neutral-0 px-2 py-1 hover:opacity-60 transition-opacity"
                        >
                            VIEW
                        </Link>
                        {user && (
                            <Link 
                                to={`/fork/${recipe.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity"
                            >
                                FORK
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Don't return early for loading/error - let animation show first

    return (
        <div className="min-h-screen bg-neutral-50 relative overflow-hidden">
            {/* Floating Create Button */}
            {user && <FloatingCreateButton />}
            
            {/* Full-page animation overlay */}
            {shouldShowAnimation && !animationComplete && (
                <div className={`fixed inset-0 z-50 transition-all duration-[800ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] cursor-pointer ${
                    animationPhase >= 3 ? 'opacity-0 translate-y-[-10px] pointer-events-none' : 'opacity-100 translate-y-0'
                }`} onClick={skipAnimation}>
                    {/* Animated background elements */}
                    <div className="absolute inset-0 bg-neutral-50">
                        {/* Subtle background pattern */}
                        <div className={`absolute inset-0 transition-all duration-[600ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${
                            animationPhase >= 1 ? 'opacity-10' : 'opacity-0'
                        }`}>
                            <div className="absolute inset-0" style={{
                                backgroundImage: `radial-gradient(circle at 25% 25%, #1b263b 2px, transparent 2px),
                                                 radial-gradient(circle at 75% 75%, #1b263b 2px, transparent 2px)`,
                                backgroundSize: '60px 60px',
                                backgroundPosition: '0 0, 30px 30px'
                            }} />
                        </div>
                        
                    </div>
                    
                    {/* Central animated content */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center px-4 max-w-4xl mx-auto">
                            {/* Main title */}
                            <div className={`transition-all duration-[800ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${
                                animationPhase >= 2 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                            }`}>
                                <h1 className="text-5xl md:text-6xl leading-[0.85] font-sans text-neutral-900 tracking-tight font-medium mb-6 md:mb-8">
                                    4RKED
                                </h1>
                            </div>
                            
                            {/* Cooking tips display */}
                            <div className={`transition-all duration-[800ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] delay-200 ${
                                animationPhase >= 2 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'
                            }`}>
                                <div className="mb-6">
                                    <div className="flex items-center justify-center mb-4">
                                        <span className="text-2xl mr-3">💡</span>
                                        <h2 className="font-mono uppercase tracking-wider text-sm text-neutral-900 opacity-60">
                                            Kitchen Tip #{currentTipIndex + 1}
                                        </h2>
                                    </div>
                                    
                                    <div className="relative h-20 md:h-24 flex items-center justify-center overflow-hidden">
                                        <p className={`font-sans text-base md:text-lg text-neutral-900 leading-relaxed transition-all duration-700 ease-in-out ${
                                            animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'
                                        }`} key={currentTipIndex}>
                                            {cookingTips[currentTipIndex]}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Subtitle */}
                            <div className={`transition-all duration-[800ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] delay-400 ${
                                animationPhase >= 2 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'
                            }`}>
                                <p className="font-mono uppercase tracking-wider text-base md:text-lg text-neutral-900 opacity-60 max-w-2xl mx-auto mb-4">
                                    Turn Ideas into Meals
                                </p>
                            </div>
                            
                            {/* Click hint */}
                            <div className={`transition-all duration-[600ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] delay-600 ${
                                animationPhase >= 2 ? 'opacity-40 translate-y-0' : 'opacity-0 translate-y-4'
                            }`}>
                                <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                    Click anywhere to skip
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main content - appears after animation */}
            <div className={`w-full px-0 py-2 md:py-4 transition-all duration-[600ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${
                (animationPhase >= 4 && showContent) || pageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
                {/* Loading state */}
                {(loading || isFiltering) && (
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <PixelArtLoader 
                            message={loading ? 'LOADING RECIPES' : 'PROCESSING RECIPES'}
                            subMessage={loading ? 'Discovering culinary creations...' : 'Curating the best recipes...'}
                        />
                    </div>
                )}



                {/* Error state */}
                {error && (
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Error</Heading>
                        <Text>{error}</Text>
                    </div>
                )}

                {/* Main content - only show when not loading, not filtering, and no error */}
                {!loading && !isFiltering && !error && (
                    <>
                        {/* Header */}
                        <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative">
                            {/* Controls Button - Bottom Right of Heading */}
                            <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 z-10 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setControlsExpanded((prev) => !prev)}
                                    aria-expanded={controlsExpanded}
                                    aria-controls={controlsPanelId}
                                    aria-label={`${controlsExpanded ? 'Hide' : 'Show'} explore controls`}
                                    className="flex items-center justify-center w-10 h-10 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                                >
                                    <span className="text-xl leading-none">{controlsExpanded ? '▴' : '▾'}</span>
                                </button>
                            </div>
                            
                            <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                                <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                                    EXPLORE
                                </h1>
                                <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                                    DISCOVER RECIPES • {filteredRecipes.length} RECIPE{filteredRecipes.length !== 1 ? 'S' : ''}
                                </p>
                            </div>
                        </div>

                {/* Main Content */}
                <div className="bg-neutral-0 hairline minimal-padding vspace">
                    {controlsExpanded && (
                        <div id={controlsPanelId} className="space-y-6 mb-6">
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                <Heading level={3}>Recipe Collection</Heading>
                                <div className="flex flex-wrap gap-2 md:gap-3">
                                    <Link 
                                        to="/bases" 
                                        className="px-3 md:px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-xs md:text-sm uppercase tracking-wider touch-target"
                                    >
                                        BROWSE BASES
                                    </Link>
                                </div>
                            </div>

                            {/* Ultra-Compact Filters Row */}
                            <div className="flex flex-wrap items-center gap-1.5">
                                {/* Search Toggle */}
                                <button
                                    type="button"
                                    onClick={() => setShowSearch(!showSearch)}
                                    className={`px-2 py-1 font-mono text-xs uppercase tracking-wider transition-colors ${
                                        searchTerm 
                                            ? 'bg-neutral-900 text-neutral-0' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    🔍
                                </button>

                                {/* Category Filter */}
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="px-2 py-1 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider"
                                >
                                    <option value="all">CAT</option>
                                    <option value="Breakfast">BREAKFAST</option>
                                    <option value="Brunch">BRUNCH</option>
                                    <option value="Lunch">LUNCH</option>
                                    <option value="Dinner">DINNER</option>
                                    <option value="Snack">SNACK</option>
                                    <option value="Dessert">DESSERT</option>
                                    <option value="Supper">SUPPER</option>
                                    <option value="Appetizer">APPETIZER</option>
                                    <option value="Side">SIDE</option>
                                    <option value="Drink">DRINK</option>
                                    <option value="Condiment">CONDIMENT</option>
                                    <option value="Bread">BREAD</option>
                                    <option value="Salad">SALAD</option>
                                    <option value="Soup">SOUP</option>
                                </select>

                                {/* Cooking Time Filter */}
                                <select
                                    value={cookingTimeFilter}
                                    onChange={(e) => setCookingTimeFilter(e.target.value)}
                                    className="px-2 py-1 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider"
                                >
                                    <option value="all">TIME</option>
                                    <option value="quick">≤30M</option>
                                    <option value="medium">31-60M</option>
                                    <option value="long">&gt;60M</option>
                                </select>

                                {/* View Mode Toggle */}
                                <div className="flex items-center">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('grid')}
                                        className={`px-2 py-1 font-mono text-xs uppercase tracking-wider transition-colors ${
                                            viewMode === 'grid' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        ⚏
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('list')}
                                        className={`px-2 py-1 font-mono text-xs uppercase tracking-wider transition-colors ${
                                            viewMode === 'list' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        ☰
                                    </button>
                                </div>

                                {/* Clear Filters */}
                                {hasActiveFilters && (
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                                    >
                                        ✕
                                    </button>
                                )}

                                {/* Results Count */}
                                <span className="text-xs font-mono text-neutral-900 opacity-60 ml-1">
                                    {filteredRecipes.length}/{recipes.length}
                                </span>
                            </div>

                            {/* Search Bar - Hidden by default */}
                            {showSearch && (
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        placeholder="Search recipes..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-2 py-1 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {!controlsExpanded && hasActiveFilters && (
                        <div className="mb-3 text-xs md:text-sm font-mono text-neutral-900 opacity-60 flex items-center justify-between gap-2">
                            <span>Showing {filteredRecipes.length} of {recipes.length} recipes</span>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                            >
                                CLEAR
                            </button>
                        </div>
                    )}

                    {/* Content */}
                    {filteredRecipes.length === 0 ? (
                        <div className="text-center py-8 md:py-12">
                            <Text className="mb-4">
                                {recipes.length === 0 
                                    ? 'No recipes available yet.' 
                                    : 'No recipes match your search.'
                                }
                            </Text>
                            {recipes.length > 0 && (
                                <button onClick={() => setSearchTerm('')} className="nav-link">CLEAR SEARCH</button>
                            )}
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {filteredRecipes.map((recipe) => (
                                <RecipeGridCard key={recipe.id} recipe={recipe} />
                            ))}
                        </div>
                    ) : (
                        <div className="px-0">
                            {/* Column Headers - Hidden on smallest screens */}
                            <div className="hidden sm:grid grid-cols-4 gap-4 mb-4 px-0" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TITLE
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TAGS
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    CREATOR
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    ACTIONS
                                </div>
                            </div>

                            {/* Table - Extended to edges */}
                            <div className="bg-neutral-0 sm:bg-transparent">
                                {filteredRecipes.map((recipe, index) => (
                                    <div key={recipe.id} className={index > 0 ? 'mt-2' : ''}>
                                        <RecipeListItem recipe={recipe} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Popup Windows */}
                {openPopups.map((popup) => (
                    <PopupWindow
                        key={popup.id}
                        title={popup.recipe.name || popup.recipe.title}
                        images={popup.recipe.images || []}
                        onClose={() => closePopup(popup.id)}
                        style={popup.style}
                    />
                ))}

                    </>
                )}
            </div>
        </div>
    );
};

export default Explore;
